import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
} from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ReactElement, useMemo, useState } from "react";
import * as Yup from "yup";

import { UploadType } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { JobError } from "~frontend/components/forms/CsvUpload/JobError";
import { useCsvUpload } from "~frontend/components/forms/CsvUpload/useCsvUpload";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikDateTimePicker from "~frontend/components/forms/FormikDateTimePicker";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme from "~frontend/theme";
import { useStaffCsvFilenameValidator } from "~frontend/util/useStaffCsvFilenameValidator";

import { FormikDialog } from "../forms/FormikDialog";
import FormikSwitchField from "../forms/FormikSwitchField";
import { useOptOutSummary } from "./useOptOutSummary";

const OPT_OUT_UPLOAD_LIMIT = 10 * 1000 * 1000; // 10 million
const MANUAL_IMPORT_PHONE_NUMBER_COLUMN = "phoneNumber";

type NewOptOutListInput = {
  name: string;
  type: OptOutListType;
  uploadMethod: string;
  phoneNumberColumn: string;
  expiresAt: DateTime | null;
  skipAlreadyOptedOut: boolean;
  manualCsv: string | null;
};

export type NewOptOutListPrepopulatedSettings = Partial<
  Pick<NewOptOutListInput, "name" | "type" | "expiresAt">
>;

type NewAdminListProps = {
  isOpen: boolean;
  onClose: () => void;
  defaultSettings?: NewOptOutListPrepopulatedSettings;
};

export type OptOutListType = "PERMANENT" | "TEMPORARY";

function createOptOutListCsv(rows: string[]): File {
  // add a header to the csv
  rows.unshift(MANUAL_IMPORT_PHONE_NUMBER_COLUMN);

  // create a blob from the submitted text
  const blob = new Blob([rows.join("\n")], {
    type: "text/csv",
  });
  return new File([blob], "text-entry.csv", {
    type: "text/csv",
  });
}

export const NewAdminList = ({
  isOpen,
  onClose,
  defaultSettings = {},
}: NewAdminListProps): ReactElement => {
  const subaccountInfo = useSubaccount();
  const isGlobal = !subaccountInfo?.subaccount;

  const { enqueueSnackbar } = useSnackbar();

  const [overwritingCsv, setOverwritingCsv] = useState(false);

  const { createList } = useOptOutSummary({
    subaccountId: subaccountInfo?.subaccount?.id,
  });

  const [importError, setImportError] = useState<string | null>(null);

  const validator = useStaffCsvFilenameValidator({ orgWide: isGlobal });

  const {
    renderCsvUpload,
    csvState,
    startCsvUpload,
    setProcessingState,
    onClearFile,
    isOverwritingInProgress: isCsvDirty,
    startManualCsvUpload,
  } = useCsvUpload({
    overrideContactLimit: OPT_OUT_UPLOAD_LIMIT,
    allowPhoneNumberOnlyColumn: true,
    uploadType: UploadType.OptOutList,
    overwriting: overwritingCsv,
    onChangeOverwriting: setOverwritingCsv,
    renderProcessingState: () => {
      switch (csvState) {
        case "CSV_IMPORT_FAILED":
          return (
            <JobError
              errorMessage={
                importError || "Error importing file. Please try again."
              }
            />
          );
      }

      return <></>;
    },
    validCsvFilename: validator,
  });

  const isUploading = csvState === "UPLOADING_CSV";

  const nextWeek = useMemo(() => {
    return DateTime.now().plus({ days: 7 });
  }, []);

  return (
    <FormikDialog<NewOptOutListInput>
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      initialValues={{
        name: defaultSettings.name || "",
        type: defaultSettings.type || "PERMANENT",
        uploadMethod: "CSV",
        phoneNumberColumn: "",
        expiresAt: defaultSettings.expiresAt || nextWeek,
        skipAlreadyOptedOut: true,
        manualCsv: "",
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required("Required").min(1),
        uploadMethod: Yup.string().required("Required"),
        phoneNumberColumn: Yup.string()
          .optional()
          .when("uploadMethod", {
            is: "CSV",
            then: Yup.string().required("Required"),
          }),
        expiresAt: Yup.date()
          .optional()
          .when("type", {
            is: "TEMPORARY",
            then: Yup.date()
              .min(new Date(), "Expiration date must be in the future")
              .required("Required"),
          }),
        manualCsv: Yup.string()
          .optional()
          .when("uploadMethod", {
            is: "ManualEntry",
            then: Yup.string()
              .matches(/^[\d+\-.() \n]+$/, "Has invalid characters")
              .required("Required"),
          }),
      })}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        setImportError(null);
        let uploadMetadata;
        let handleError = (message: string) => {
          setSubmitting(false);
          enqueueSnackbar(message, { variant: "error" });
        };

        if (values.uploadMethod === "CSV") {
          uploadMetadata = await startCsvUpload({
            phoneNumberColumn: values.phoneNumberColumn,
          });

          handleError = (message: string) => {
            setSubmitting(false);
            setProcessingState("CSV_IMPORT_FAILED");
            setImportError(message);
            setOverwritingCsv(true);
          };
        } else if (values.uploadMethod === "ManualEntry") {
          const cleanTextEntries = (values.manualCsv || "")
            .split("\n")
            .filter(row => row.length)
            .map(row => row.slice(0, 25));

          if (!cleanTextEntries.length) {
            setSubmitting(false);
            return null;
          }

          if (cleanTextEntries.length > OPT_OUT_UPLOAD_LIMIT) {
            setSubmitting(false);
            enqueueSnackbar(
              `CSV exceeds import limit of ${OPT_OUT_UPLOAD_LIMIT.toLocaleString()} rows. Please upload a CSV below this row size limit.`,
              { variant: "error" }
            );
            return null;
          }

          const generatedCsv = createOptOutListCsv(cleanTextEntries);

          values.phoneNumberColumn = MANUAL_IMPORT_PHONE_NUMBER_COLUMN;
          uploadMetadata = await startManualCsvUpload({ generatedCsv });
        }

        if (uploadMetadata) {
          const { errors } = await createList({ uploadMetadata, values });

          if (errors) {
            handleError("Error importing list. Please try again.");
            return;
          }

          enqueueSnackbar("List created", { variant: "success" });
          onClearFile();
          resetForm();
          onClose();
        } else {
          handleError("Error uploading file. Please try again.");
        }
      }}
    >
      {({ values, dirty, isSubmitting, isValid, setFieldValue }) => {
        const tooltipText = () => {
          if (values.uploadMethod === "CSV" && csvState === "NO_CSV") {
            return "Please select a CSV";
          }

          if (!dirty || !isValid) {
            return "All fields must be filled out";
          }

          return null;
        };

        return (
          <>
            <RouteLeavingGuard when={isCsvDirty || dirty} />

            <DialogTitle>Add a New List</DialogTitle>
            <DialogContent>
              <FlexBox flexDirection="column" gap={theme.spacing(1)}>
                <FormikTextField name="name" label="List Name" />

                <FormikSelect name="uploadMethod" label="Upload Method">
                  <MenuItem value="CSV">CSV</MenuItem>
                  <MenuItem value="ManualEntry">Text Entry</MenuItem>
                </FormikSelect>

                {values.uploadMethod === "CSV" ? (
                  renderCsvUpload(setFieldValue)
                ) : (
                  <FormikTextField
                    minRows={3}
                    maxRows={20}
                    multiline={true}
                    name="manualCsv"
                    label="Phone Number List"
                    bottomLabel="Enter a newline-separated list of phone numbers"
                  />
                )}

                <FormikSelect name="type" label="List Type">
                  <MenuItem value="PERMANENT">Opt Out</MenuItem>
                  {!isGlobal && (
                    <MenuItem value="TEMPORARY">Temporary Opt Out</MenuItem>
                  )}
                </FormikSelect>

                {values.type === "TEMPORARY" && (
                  <FormikDateTimePicker
                    fullWidth
                    minDate={DateTime.now()}
                    minDateError="Expiration date must be in the future"
                    name="expiresAt"
                    label="List Expiration Date"
                    helperText="Contacts on this list won't be messaged until after this date"
                  />
                )}

                <FormikSwitchField
                  name="skipAlreadyOptedOut"
                  label="Skip duplicates (only add contacts that don't appear on existing opt out lists)"
                />
              </FlexBox>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isUploading || isSubmitting}
                onClick={onClose}
                variant="contained"
                color="tertiary"
              >
                Cancel
              </Button>
              <FormikSubmitButton
                disabled={!isValid || isUploading}
                submitText="Upload"
                loadingText="Uploading..."
                tooltipText={tooltipText()}
              />
            </DialogActions>
          </>
        );
      }}
    </FormikDialog>
  );
};
